import { useRef } from 'react'
import { Node } from 'prosemirror-model'
import { EditorView, NodeView, Decoration } from 'prosemirror-view'

import Styles from './File.module.scss'
import { Maybe } from 'types'
import { FileInput } from './FileInput'
import { AttachedFile } from './AttachedFile'
import { createRoot, Root } from 'react-dom/client'

type FileAttributes = { url: string; title: string; meta: string }
interface Props {
  attrs: FileAttributes
  updateAttrs: (attrs: { [key: string]: string | number }) => void
}

const FileView = ({ attrs, updateAttrs }: Props) => {
  const fileCardRef = useRef<Maybe<HTMLDivElement>>(null)

  return (
    <>
      {attrs.url ? (
        <AttachedFile fileCardRef={fileCardRef} url={attrs.url} title={attrs.title} meta={attrs.meta} />
      ) : (
        <FileInput fileCardRef={fileCardRef} attrs={attrs} updateAttrs={updateAttrs} />
      )}
    </>
  )
}

const ReadFileView = ({ attrs }: { attrs: { [key: string]: any } }) => {
  const fileCardRef = useRef<Maybe<HTMLDivElement>>(null)

  return (
    <>{attrs.url && <AttachedFile fileCardRef={fileCardRef} url={attrs.url} title={attrs.title} meta={attrs.meta} />}</>
  )
}

export class Download implements NodeView {
  dom: HTMLElement
  readOnly?: boolean

  node: Node
  view: EditorView
  getPos: () => number
  decorations: Decoration[]
  attrs: FileAttributes
  root: Root

  constructor(node: Node, view: EditorView, getPos: () => number, decorations: Decoration[], readOnly?: boolean) {
    this.attrs = node.attrs as FileAttributes
    this.node = node
    this.view = view
    this.getPos = getPos
    this.decorations = decorations
    this.readOnly = readOnly
    this.dom = document.createElement('div')
    this.dom.dataset.nodeType = this.node.type.name
    this.root = createRoot(this.dom)

    this.render()
  }

  private render = () => {
    this.root.render(
      this.readOnly ? (
        <ReadFileView attrs={this.attrs} />
      ) : (
        <FileView attrs={this.attrs} updateAttrs={this.dispatchUpdatedAttrs} />
      ),
    )
  }

  selectNode = () => {
    this.dom.classList.add(Styles.selected)
  }

  deselectNode = () => {
    this.dom.classList.remove(Styles.selected)
  }

  dispatchUpdatedAttrs = (updatedAttrs: { [key: string]: string | number }) => {
    const newAttrs = { ...this.attrs, ...updatedAttrs }
    const tx = this.view.state.tr.setNodeMarkup(this.getPos(), undefined, newAttrs)
    this.attrs = newAttrs
    this.view.dispatch(tx)
  }

  update(node: Node) {
    if (node.type !== this.node.type) return false
    this.node = node
    this.attrs = node.attrs as FileAttributes
    this.render()
    return true
  }

  stopEvent(e: Event) {
    if (this.readOnly) return false
    // Allow view to be dragged
    if (e.type.includes('drag')) return false
    return true
  }

  destroy() {
    this.root.unmount()
  }
}
